import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Card } from '../card/card';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { Avatar } from '../avatar/avatar';
import { ZagDatePipe } from '../formatting/zag-date.pipe';
import { UserListRowData } from '../user-list-row/user-list-row';

/**
 * Re-export UserListRowData as UserCardData for consistency
 */
export type UserCardData = UserListRowData;

/**
 * Data structure for a pending invitation in UserCard
 */
export interface UserCardInvitationData {
  type: 'invitation';
  id: string;
  email: string;
  name?: string | null;
  /** ISO date string of when the invitation was sent */
  invitedAt: string;
}

/**
 * Union type accepted by the UserCard input — covers both active users and pending invitations.
 */
export type UserCardAnyData = (UserCardData & { type: 'user' }) | UserCardInvitationData;

/**
 * UserCard Component
 *
 * Displays a user or a pending invitation in a card format.
 * Uses the Card component for consistent styling.
 *
 * In **user mode** (`type: 'user'`) it shows name, email, optional deactivated badge,
 * roles/groups/person-link stats, and an activate/deactivate action button.
 *
 * In **invitation mode** (`type: 'invitation'`) it shows email (or name if available),
 * an "Invitation pending" badge, the sent-at date, and resend/delete action buttons.
 *
 * @example
 * ```html
 * <!-- User mode -->
 * <zag-user-card
 *   [user]="userData"
 *   (cardClick)="onUserSelected($event)"
 *   (statusToggle)="onToggleStatus($event)">
 * </zag-user-card>
 *
 * <!-- Invitation mode -->
 * <zag-user-card
 *   [user]="invitationData"
 *   (cardClick)="onOpenInvitation($event)"
 *   (resendInvitation)="onResend($event)"
 *   (deleteInvitation)="onDelete($event)">
 * </zag-user-card>
 * ```
 */
@Component({
  selector: 'zag-user-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, TranslatePipe, ZagButton, ZagIcon, Avatar, ZagDatePipe],
  host: {
    '[class.zag-user-card]': 'true',
  },
  template: `
    <zag-card variant="interactive" (cardClick)="onCardClick()">
      <!-- Header: Avatar, Name/Email and Action buttons -->
      <div card-header class="zag-user-card__header">
        @if (user().type === 'invitation') {
          <zag-avatar [name]="inv().name ?? inv().email" size="md" />
          <div class="zag-user-card__text">
            <div class="zag-user-card__title-wrapper">
              <h3 class="zag-card-title" [title]="inv().email">{{ inv().name ?? inv().email }}</h3>
            </div>
            <span class="zag-user-card__badge zag-user-card__badge--pending">{{ 'settings.users.invite.pending.badge' | translate }}</span>
          </div>
          @if (canManageInvitations()) {
            <div class="zag-user-card__actions">
              <button
                zagButton="basic"
                zagType="secondary"
                zagSize="sm"
                type="button"
                class="zag-user-card__action-btn"
                [attr.aria-label]="'settings.users.actions.resend' | translate"
                [title]="'settings.users.actions.resend' | translate"
                (click)="onResendClick($event)"
              >
                <span zagIcon="forward_to_inbox" aria-hidden="true"></span>
              </button>
              <button
                zagButton="basic"
                zagType="secondary"
                zagSize="sm"
                type="button"
                class="zag-user-card__action-btn zag-user-card__action-btn--danger"
                [attr.aria-label]="'settings.users.actions.revoke' | translate"
                [title]="'settings.users.actions.revoke' | translate"
                (click)="onDeleteClick($event)"
              >
                <span zagIcon="delete" aria-hidden="true"></span>
              </button>
            </div>
          }
        } @else {
          <zag-avatar [name]="reg().name" [src]="reg().image ?? undefined" size="md" />
          <div class="zag-user-card__text">
            <div class="zag-user-card__title-wrapper">
              <h3 class="zag-card-title">{{ reg().name }}</h3>
              @if (reg().banned) {
                <span class="zag-user-card__badge zag-user-card__badge--deactivated">{{
                  'settings.users.status.deactivated' | translate
                }}</span>
              }
            </div>
            <span class="zag-user-card__email">{{ reg().email }}</span>
          </div>
          @if (canToggleStatus()) {
            <div class="zag-user-card__actions">
              <button
                zagButton="basic"
                zagType="secondary"
                zagSize="sm"
                type="button"
                class="zag-user-card__action-btn"
                [attr.aria-label]="
                  reg().banned ? ('settings.users.actions.activate' | translate) : ('settings.users.actions.deactivate' | translate)
                "
                (click)="onToggleStatusClick($event)"
              >
                <span [zagIcon]="reg().banned ? 'check_circle' : 'block'" aria-hidden="true"></span>
              </button>
            </div>
          }
        }
      </div>

      <!-- Body: Stats (user mode only) -->
      <div class="zag-user-card__stats">
        @if (user().type === 'user') {
          <span class="zag-user-card__stat">
            <span zagIcon="security"></span>
            {{ reg().directRoles.length }} {{ rolesLabel() }}
          </span>
          <span class="zag-user-card__stat">
            <span zagIcon="groups"></span>
            {{ reg().groupCount }} {{ groupsLabel() }}
          </span>
          @if (!hideLinkStatus()) {
            @if (reg().hasLinkedPerson) {
              <span class="zag-user-card__stat zag-user-card__stat--linked">
                <span zagIcon="link"></span>
                {{ linkedLabel() }}
              </span>
            } @else {
              <span class="zag-user-card__stat zag-user-card__stat--not-linked">
                <span zagIcon="link_off"></span>
                {{ notLinkedLabel() }}
              </span>
            }
          }
        } @else if (user().type === 'invitation') {
          <span class="zag-user-card__stat">
            <span zagIcon="calendar-today"></span>
            {{ inv().invitedAt | zagDate }}
          </span>
        }
      </div>
    </zag-card>
  `,
  styles: [
    `
      .zag-user-card {
        display: flex;
        flex: 1;
        align-content: stretch;
        flex-wrap: nowrap;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
      }

      .zag-user-card .zag-card {
        width: 100%;
      }

      .zag-user-card__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
      }

      .zag-user-card__text {
        flex: 1;
        min-width: 0;
      }

      .zag-user-card__title-wrapper {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
      }

      .zag-user-card__badge {
        display: inline-flex;
        padding: 2px 8px;
        border-radius: var(--border-radius-sm);
        font-size: var(--font-size-xs);
        font-weight: 500;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .zag-user-card__badge--deactivated {
        background-color: var(--color-warning-light);
        color: var(--color-warning);
      }

      .zag-user-card__badge--pending {
        background-color: var(--color-info-light);
        color: var(--color-info);
      }

      .zag-user-card__email {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-user-card__stats {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
        margin-top: var(--spacing-sm);
      }

      .zag-user-card__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);

        [zagIcon] {
          font-size: 16px;
        }
      }

      .zag-user-card__stat--linked {
        color: var(--color-success);
      }

      .zag-user-card__stat--not-linked {
        color: var(--color-error);
      }

      /* Actions wrapper — pushed to the right; holds one or more action buttons */
      .zag-user-card__actions {
        margin-left: auto;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
      }

      .zag-user-card__action-btn.zag-button {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        color: var(--text-color-secondary);
        border: none;
        background: transparent;
        border-radius: var(--border-radius-sm);
        cursor: pointer;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;

        &:hover:not(:disabled) {
          background-color: var(--surface-color-hover);
          color: var(--color-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-user-card__action-btn--danger {
        &:hover:not(:disabled) {
          background-color: var(--color-error-bg, #fde8e8);
          color: var(--color-error);
        }
      }
    `,
  ],
})
export class UserCard {
  /** The user or invitation data to display */
  readonly user = input.required<UserCardAnyData>();

  /** Label for roles (e.g., "roles") — user mode only */
  readonly rolesLabel = input('roles');

  /** Label for groups (e.g., "groups") — user mode only */
  readonly groupsLabel = input('groups');

  /** Label for linked status (e.g., "Linked") — user mode only */
  readonly linkedLabel = input('Linked');

  /** Label for not linked status (e.g., "Not linked") — user mode only */
  readonly notLinkedLabel = input('Not linked');

  /** Whether to hide the person link status — user mode only */
  readonly hideLinkStatus = input(false);

  /** Whether the user status mutation control is available. */
  readonly canToggleStatus = input(true);

  /** Whether invitation mutation controls are available. */
  readonly canManageInvitations = input(true);

  /** Emitted when the card body is clicked */
  readonly cardClick = output<UserCardAnyData>();

  /** Emitted when the activate/deactivate button is clicked — user mode only */
  readonly statusToggle = output<UserCardData>();

  /** Emitted when the resend invitation button is clicked — invitation mode only */
  readonly resendInvitation = output<UserCardInvitationData>();

  /** Emitted when the delete invitation button is clicked — invitation mode only */
  readonly deleteInvitation = output<UserCardInvitationData>();

  /**
   * Type-safe view of the invitation data.
   * Only access within `@if (user().type === 'invitation')` template blocks.
   */
  protected readonly inv = computed(() => this.user() as UserCardInvitationData);

  /**
   * Type-safe view of the regular user data.
   * Only access within `@else` / `@if (user().type === 'user')` template blocks.
   */
  protected readonly reg = computed(() => this.user() as UserCardData & { type: 'user' });

  /** Handle card click */
  onCardClick(): void {
    this.cardClick.emit(this.user());
  }

  /** Handle status toggle button click (user mode) */
  onToggleStatusClick(event: Event): void {
    event.stopPropagation();
    this.statusToggle.emit(this.user() as UserCardData);
  }

  /** Handle resend invitation button click (invitation mode) */
  onResendClick(event: Event): void {
    event.stopPropagation();
    this.resendInvitation.emit(this.user() as UserCardInvitationData);
  }

  /** Handle delete invitation button click (invitation mode) */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteInvitation.emit(this.user() as UserCardInvitationData);
  }
}
